/**
 * Appointments drafting engine — the redraft-first technical draft (TO / SJ / TJ).
 *
 * Authority:
 *   - `docs/specs/control-centre/03-appointments.md` §A4 (the constraint set), §A8
 *     (`Draft All`, redraft-first) and Part C (shared invariants);
 *   - `docs/specs/rulings-2026-08-11-open-questions.md` §B, ruling **CC-Q3**;
 *   - `docs/specs/control-centre/07-configuration.md` §06 (the configurable thresholds:
 *     `Judge SJ / TJ Balance Tolerance` = 2, `Team Exposure Heat Threshold` = 3,
 *     `TO Appointments Per Day` = 2).
 *
 * **CC-Q3 — redraft first, warn only as a fallback.** A draft that carries an SJ/TJ
 * imbalance is never handed to the TD with a badge on it. The engine drafts, measures the
 * spread against the configured tolerance and **re-draws until the spread is inside
 * tolerance or provably stuck**. Only a residual, unresolvable imbalance reaches
 * {@link DraftResult.warnings}, and it is marked `attention` so the surface can flag it
 * prominently. Everything the redraft loop could fix is fixed before the caller sees it.
 *
 * **Imbalance definition — taken from the spec, not from a local default.** Rule 4 of
 * §A4 defines the imbalance per judge: *"Warn where a judge's SJ and TJ counts differ by
 * more than the tolerance. Default tolerance 2."* So a judge is imbalanced when
 * `|SJ − TJ| > judge_sj_tj_tolerance`. The engine therefore uses the per-official
 * difference, **not** a max−min spread across the panel; the build brief's fallback of
 * "max−min spread > 1" is superseded because the spec is explicit and the spec wins.
 *
 * **Purity.** No I/O, no clock, no randomness. Every ordering decision is either derived
 * from the inputs or supplied explicitly through {@link DraftInput.official_order} and
 * {@link DraftInput.rotation_offset}, so the same inputs always produce the same draft.
 *
 * That holds for the offset's two effects, added in RUN 38 so that `Re-Draft` produces a
 * genuinely different arrangement rather than a byte-identical one: it starts the greedy walk
 * at a different fixture ({@link fixtureVisitOrder}) and it permutes the officials' tie-break
 * ranking through a small pure string hash ({@link buildOrderIndex}). Neither reads a clock or
 * a random source — the hash is a total function of the id and the offset — so a given offset
 * over a given schedule is as reproducible as offset 0 has always been, and **offset 0 is the
 * identity**: it is the plain schedule walk and the plain alphabetical ranking, byte for byte
 * what this engine produced before RUN 38.
 *
 * British English throughout, per the standing copy rule.
 */
/** Draft order within a match: the person in charge first, then the two judges. */
export const TECHNICAL_SLOTS = ["TO", "SJ", "TJ"];
/**
 * The unconfigured tournament's rule set. **Every rule switch defaults to on**, for the same
 * reason every threshold defaults to its §06 printed value: a tournament nobody has
 * configured yet drafts under the full rule set rather than under none of it. A missing row
 * means "not decided", and the safe reading of "not decided" is the rule as the spec prints
 * it.
 */
export const DEFAULT_ENGINE_CONFIG = {
    judge_sj_tj_tolerance: 2,
    to_appointments_per_day: 2,
    judge_appointments_per_day: null,
    team_exposure_heat: 3,
    co_appointment_pair_tolerance: 3,
    country_conflict_enabled: true,
    country_conflict_hard_for_drafts: true,
    atds_excluded_from_ai_drafts: true,
    judge_sj_tj_balance_enabled: true,
    team_exposure_rotation_enabled: true,
    allow_judges_in_to_slot: false,
    allow_to_officials_in_judge_slots: false,
    max_redraft_passes: 64,
};
/* ────────────────────────────────────────────────────────────────────────────
 * 1. Deterministic ordering helpers
 * ──────────────────────────────────────────────────────────────────────────── */
/** Plain lexicographic comparison — no locale, so no host-dependent ordering. */
function compareStrings(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * Fixtures are drafted in schedule order: date, then kick-off, then Men's before Women's,
 * then match number, then the database id. Every component is an input value, so the order
 * cannot drift between runs.
 */
export function compareFixtures(a, b) {
    return (compareStrings(a.date, b.date) ||
        compareStrings(a.time, b.time) ||
        compareStrings(a.competition, b.competition) ||
        compareStrings(a.match_no, b.match_no) ||
        a.tms_match_db_id - b.tms_match_db_id);
}
/**
 * FNV-1a, 32 bits — a small, pure string hash, used for nothing but ordering.
 *
 * **Exported since RUN 40, and only so the umpire engine can read this one.** It was private
 * while it had one caller. When `umpire-engine.ts` took the same RUN 38b treatment, the choice
 * was between a second copy of FNV-1a and one shared function; a copy would have meant two
 * permutations that were the same today and free to drift apart on any later edit, and the
 * two engines' variants must be built the same way or "variant 2" means two different things
 * on the two pages. It stays an ordering helper and nothing else.
 *
 * It is FNV-1a rather than anything cleverer because the only properties wanted are that
 * it is **deterministic**, **cheap**, and **spreads small input changes across the whole
 * output**. It is not cryptographic and nothing here needs it to be: it decides who is
 * considered first when two officials are otherwise exactly equal, and the constraint set
 * decides everything that matters.
 *
 * `Math.imul` and `>>> 0` keep the arithmetic in 32-bit unsigned space on every engine, so
 * the same string hashes to the same number in a browser, in Node and in a test runner.
 */
export function hash32(text) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}
/**
 * Rank of each official for the final tie-break, honouring `official_order` and the offset.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A PERMUTATION AND NOT A ROTATION (RUN 38)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It used to rotate the ranking by one place per offset: `(position - offset) mod size`. That
 * is a very weak reshuffle — it moves every official one step along a fixed cycle and leaves
 * the *relative* order of all but one adjacent pair exactly as it was. Combined with this
 * index being the LAST component of the score vector, which fires only when a candidate's
 * workload, team exposure, colleague heat and per-day count are all exactly equal, the
 * practical effect on a real schedule was nil: `Re-Draft` produced byte-identical drafts, and
 * the Technical Delegate pressed it three times to prove it.
 *
 * So the offset now selects a whole **permutation** of the ranking: officials are ranked by
 * `hash32(id|offset)`, with the id itself as the final tie-break so the map is total and
 * stable. Every relative rank can move, not one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OFFSET 0 IS THE IDENTITY, AND THAT IS A HARD REQUIREMENT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Offset 0 returns the plain positional ranking — `official_order` first, then ascending id —
 * exactly as this function always has. Every draft the system has ever produced was offset 0,
 * every existing test asserts against it, and the page's plain `Draft The Pool Stage` still
 * sends no offset at all. A change that shuffled offset 0 would silently rewrite the meaning
 * of every one of those. It is asserted in the engine's own tests against a pinned snapshot.
 *
 * The offset is NOT reduced modulo the panel size, unlike the rotation it replaces. A hash
 * has no cycle to wrap around, so offset 50 is its own permutation rather than offset 1's on
 * a fifty-strong panel — which is what a Technical Delegate pressing `Re-Draft` for the
 * fiftieth time is entitled to expect.
 */
function buildOrderIndex(officials, explicitOrder, rotationOffset) {
    const ids = officials.map((official) => official.tms_person_id);
    const ordered = [];
    if (explicitOrder) {
        for (const id of explicitOrder) {
            if (ids.includes(id) && !ordered.includes(id))
                ordered.push(id);
        }
    }
    for (const id of [...ids].sort(compareStrings)) {
        if (!ordered.includes(id))
            ordered.push(id);
    }
    const ranked = rotationOffset === 0
        ? ordered
        : [...ordered].sort((a, b) => hash32(`${a}|${String(rotationOffset)}`) - hash32(`${b}|${String(rotationOffset)}`) ||
            compareStrings(a, b));
    const index = new Map();
    ranked.forEach((id, position) => {
        index.set(id, position);
    });
    return index;
}
function slotKey(matchId, slot) {
    return `${String(matchId)}|${slot}`;
}
/**
 * Who the engine may propose for a slot.
 *
 * **ATDs.** §A4 rule 3 keeps them out of AI drafts, and §06 gives that rule a switch. With
 * the switch On — the default — an ATD is never a candidate. With it Off, an ATD becomes a
 * candidate for any technical slot, which is precisely the set the TD may already appoint one
 * to by hand (`isRoleEligible` in `apps/api/src/repositories/appointments.ts`): switching the
 * rule off makes the engine propose what the dropdown already permits, and invents no new
 * eligibility of its own.
 */
function isEligibleForSlot(official, slot, config) {
    if (official.role === "ATD")
        return !config.atds_excluded_from_ai_drafts;
    if (official.role !== "TO" && official.role !== "Judge")
        return false;
    if (slot === "TO") {
        return official.role === "TO" || (config.allow_judges_in_to_slot && official.role === "Judge");
    }
    return (official.role === "Judge" ||
        (config.allow_to_officials_in_judge_slots && official.role === "TO"));
}
function isUnavailable(official, date) {
    return (official.unavailable_dates ?? []).includes(date);
}
function hasCountryConflict(official, fixture) {
    return official.country === fixture.home_team || official.country === fixture.away_team;
}
/** Two matches clash when they start at the same time on the same day, in either competition. */
function clashes(a, b) {
    return a.date === b.date && a.time === b.time;
}
function emptyCounters() {
    return { total: new Map(), perDay: new Map(), teamExposure: new Map(), together: new Map() };
}
function bump(map, key, by = 1) {
    map.set(key, (map.get(key) ?? 0) + by);
}
function creditAssignment(counters, officialId, fixture, matchMembers) {
    bump(counters.total, officialId);
    bump(counters.perDay, `${officialId}|${fixture.date}`);
    for (const team of [fixture.home_team, fixture.away_team]) {
        if (team !== null)
            bump(counters.teamExposure, `${officialId}|${team}`);
    }
    for (const other of matchMembers) {
        if (other === officialId)
            continue;
        bump(counters.together, `${officialId}|${other}`);
        bump(counters.together, `${other}|${officialId}`);
    }
}
/**
 * The order the greedy walk VISITS fixtures in — schedule order, started at a different
 * place for a different offset (RUN 38).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE OFFSET HAD TO REACH THE SEQUENCE AND NOT ONLY THE SCORES
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A greedy draft's outcome is decided far more by *what it has already spent* than by how it
 * breaks a tie. The very first fixture is chosen against empty counters and is therefore the
 * most freely chosen one in the schedule; everything after it is chosen against the shadow of
 * everything before. Start the same walk at fixture 7 instead of fixture 1 and the whole
 * cascade falls differently — a genuinely different arrangement, arrived at by the same rules
 * and the same scores.
 *
 * That is what `Re-Draft` needs and what the tie-break alone could not give it. See
 * {@link buildOrderIndex} for the other half of the fix and for what was wrong before.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY MOVING THE PROCESSING ORDER CANNOT WEAKEN A RULE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every filter in the candidate list is checked against **inputs or running counters**, never
 * against a fixture's position, and each one is invariant under a reordering of the walk:
 *
 *  * *Role eligibility, unavailability, country conflict* — facts about the official and the
 *    fixture. Position does not appear in them.
 *  * *Rule 1, one official one role per match* — read off that fixture's own members, and a
 *    fixture's three slots are always filled consecutively whichever place it is visited in.
 *  * *No two simultaneous matches* — checked against every booking made so far, in both
 *    directions. Reordering can change WHICH of two clashing fixtures a person takes; it
 *    cannot produce a draft in which they hold both, because the check refuses the second
 *    whichever one is second.
 *  * *Rule 7, appointments per day* — a counter tested before each assignment, so the final
 *    count cannot exceed the cap under any order.
 *
 * The soft properties — workload evenness, team exposure, colleague spread — are counter-based
 * and stay counter-based; they come out comparably even at any offset rather than identically
 * even, which is the whole point of offering a variant. What CAN differ is which slots end up
 * unfillable on an awkward schedule, and that has always been reported honestly as an
 * `unfilled_slot` warning rather than hidden.
 *
 * Offset 0 is the identity: `start` is 0 and the walk is plain schedule order, exactly as
 * before RUN 38.
 *
 * Exported since RUN 40 for the umpire engine, for the reason given on {@link hash32}: one
 * definition of "where a variant starts its walk", read by both engines.
 */
export function fixtureVisitOrder(count, rotationOffset) {
    if (count === 0)
        return [];
    const start = ((rotationOffset % count) + count) % count;
    return Array.from({ length: count }, (_, step) => (start + step) % count);
}
/**
 * Pass 1 — a load-, exposure- and colleague-aware greedy draft. It is deliberately
 * **blind to the SJ/TJ split**: balancing that split is the redraft loop's job (CC-Q3), so
 * the loop, not a lucky first pass, is what guarantees the property.
 */
function draftPass(fixtures, officials, config, locked, orderIndex, 
/** `null` = every fixture is this pass's to fill. See {@link DraftInput.draft_scope}. */
draftable, 
/** Where in the schedule this pass starts its walk. See {@link fixtureVisitOrder}. */
rotationOffset = 0) {
    const lockedByKey = new Map(locked.map((entry) => [slotKey(entry.tms_match_db_id, entry.slot), entry.official_id]));
    const slots = [];
    const counters = emptyCounters();
    /** Official → the indices of the fixtures they already hold a slot in. */
    const bookings = new Map();
    const membersByFixture = fixtures.map(() => []);
    const book = (officialId, fixtureIndex) => {
        const fixture = fixtures[fixtureIndex];
        if (fixture === undefined)
            return;
        const members = membersByFixture[fixtureIndex] ?? [];
        creditAssignment(counters, officialId, fixture, members);
        members.push(officialId);
        membersByFixture[fixtureIndex] = members;
        const held = bookings.get(officialId) ?? [];
        held.push(fixtureIndex);
        bookings.set(officialId, held);
    };
    // Locked appointments are placed first, across every fixture, so the drafted slots are
    // chosen against the full picture of what the TD has already fixed by hand.
    fixtures.forEach((fixture, fixtureIndex) => {
        for (const slot of TECHNICAL_SLOTS) {
            const lockedOfficial = lockedByKey.get(slotKey(fixture.tms_match_db_id, slot));
            if (lockedOfficial === undefined)
                continue;
            slots.push({ fixtureIndex, slot, official: lockedOfficial, locked: true });
            book(lockedOfficial, fixtureIndex);
        }
    });
    // The drafting walk, started wherever the offset says. The locked loop above deliberately
    // stays in schedule order: it places facts rather than making choices, and its counters come
    // out the same whichever order they are credited in.
    //
    // `slots` is therefore pushed in visit order rather than schedule order — which nothing
    // downstream minds, because `toResult` sorts by fixture index before it builds the
    // appointments, and every warning is sorted by `sortWarnings`. A draft's OUTPUT order is
    // schedule order at every offset; only the order it was decided in moves.
    for (const fixtureIndex of fixtureVisitOrder(fixtures.length, rotationOffset)) {
        const fixture = fixtures[fixtureIndex];
        if (fixture === undefined)
            continue;
        // Carried context: its locked appointments were placed and counted in the loop above, and
        // its empty slots are none of this pass's business. No `WorkingSlot` is pushed for them at
        // all, so they cannot be drafted, cannot be swapped by the redraft loop, cannot be
        // reported as unfilled and cannot reach the caller as an appointment to store.
        const inScope = draftable === null || draftable.has(fixture.tms_match_db_id);
        for (const slot of TECHNICAL_SLOTS) {
            if (lockedByKey.has(slotKey(fixture.tms_match_db_id, slot)))
                continue;
            if (!inScope)
                continue;
            const dailyCap = (official) => official.role === "TO" ? config.to_appointments_per_day : config.judge_appointments_per_day;
            const members = membersByFixture[fixtureIndex] ?? [];
            const candidates = officials.filter((official) => {
                const id = official.tms_person_id;
                if (!isEligibleForSlot(official, slot, config))
                    return false;
                if (isUnavailable(official, fixture.date))
                    return false;
                // Rule 1 — one official, one role per match. Hard, no override.
                if (members.includes(id))
                    return false;
                // Physically impossible — the same person at two simultaneous matches.
                const held = bookings.get(id) ?? [];
                if (held.some((other) => {
                    const otherFixture = fixtures[other];
                    return otherFixture !== undefined && clashes(otherFixture, fixture);
                })) {
                    return false;
                }
                // Rule 2 — country conflict, treated as hard for AI drafts by default. Both switches
                // are read: the rule itself (§06 `Rules In Force`) and, under it, the
                // `AI Drafts Treat As Hard` sub-toggle. With the rule Off there is no country rule in
                // force, so nothing here is filtered on it.
                if (config.country_conflict_enabled &&
                    config.country_conflict_hard_for_drafts &&
                    hasCountryConflict(official, fixture)) {
                    return false;
                }
                // Rule 7 — appointments per day.
                const cap = dailyCap(official);
                if (cap !== null && (counters.perDay.get(`${id}|${fixture.date}`) ?? 0) >= cap)
                    return false;
                return true;
            });
            if (candidates.length === 0) {
                slots.push({ fixtureIndex, slot, official: null, locked: false });
                continue;
            }
            const score = (official) => {
                const id = official.tms_person_id;
                // §06 rule `Team Exposure Rotation`. Switched off, the engine stops rotating
                // officials away from a team it has already covered — the rule is not applied, not
                // merely unreported.
                const exposure = !config.team_exposure_rotation_enabled
                    ? []
                    : [fixture.home_team, fixture.away_team]
                        .filter((team) => team !== null)
                        .map((team) => counters.teamExposure.get(`${id}|${team}`) ?? 0);
                const colleagueHeat = members.reduce((sum, other) => sum + (counters.together.get(`${id}|${other}`) ?? 0), 0);
                return [
                    counters.total.get(id) ?? 0,
                    exposure.length === 0 ? 0 : Math.max(...exposure),
                    colleagueHeat,
                    counters.perDay.get(`${id}|${fixture.date}`) ?? 0,
                    orderIndex.get(id) ?? 0,
                ];
            };
            let best = candidates[0];
            let bestScore = score(best);
            for (const candidate of candidates.slice(1)) {
                const candidateScore = score(candidate);
                if (compareScores(candidateScore, bestScore) < 0) {
                    best = candidate;
                    bestScore = candidateScore;
                }
            }
            slots.push({ fixtureIndex, slot, official: best.tms_person_id, locked: false });
            book(best.tms_person_id, fixtureIndex);
        }
    }
    return slots;
}
function compareScores(a, b) {
    const length = Math.max(a.length, b.length);
    for (let i = 0; i < length; i += 1) {
        const left = a[i] ?? 0;
        const right = b[i] ?? 0;
        if (left !== right)
            return left - right;
    }
    return 0;
}
function countJudgeDuties(slots) {
    const counts = new Map();
    for (const entry of slots) {
        if (entry.official === null)
            continue;
        if (entry.slot !== "SJ" && entry.slot !== "TJ")
            continue;
        const current = counts.get(entry.official) ?? { sj: 0, tj: 0 };
        if (entry.slot === "SJ")
            current.sj += 1;
        else
            current.tj += 1;
        counts.set(entry.official, current);
    }
    return counts;
}
/**
 * Total imbalance cost — how far, in appointments, the draft sits outside tolerance across
 * the whole judge panel. Zero means every judge satisfies rule 4.
 */
function imbalanceCost(slots, tolerance) {
    let cost = 0;
    for (const counts of countJudgeDuties(slots).values()) {
        cost += Math.max(0, Math.abs(counts.sj - counts.tj) - tolerance);
    }
    return cost;
}
/**
 * The redraft operator: exchange the SJ and TJ appointments **inside one match**.
 *
 * It is the one re-draw that can never break a hard constraint — both officials are already
 * in that match, so eligibility, availability, the clash check, the country rule, the daily
 * cap and rule 1 are all unchanged. That makes the loop safe to run to exhaustion.
 */
function bestSwap(slots, tolerance) {
    const currentCost = imbalanceCost(slots, tolerance);
    if (currentCost === 0)
        return null;
    const swappableFixtures = [...new Set(slots.map((entry) => entry.fixtureIndex))].sort((a, b) => a - b);
    let best = null;
    for (const fixtureIndex of swappableFixtures) {
        const sjIndex = slots.findIndex((entry) => entry.fixtureIndex === fixtureIndex && entry.slot === "SJ");
        const tjIndex = slots.findIndex((entry) => entry.fixtureIndex === fixtureIndex && entry.slot === "TJ");
        if (sjIndex < 0 || tjIndex < 0)
            continue;
        const sjSlot = slots[sjIndex];
        const tjSlot = slots[tjIndex];
        if (sjSlot.locked || tjSlot.locked)
            continue;
        if (sjSlot.official === null || tjSlot.official === null)
            continue;
        if (sjSlot.official === tjSlot.official)
            continue;
        const trial = slots.map((entry) => ({ ...entry }));
        const trialSj = trial[sjIndex];
        const trialTj = trial[tjIndex];
        const carry = trialSj.official;
        trialSj.official = trialTj.official;
        trialTj.official = carry;
        const cost = imbalanceCost(trial, tolerance);
        if (cost < currentCost && (best === null || cost < best.cost)) {
            best = { sjIndex, tjIndex, cost };
        }
    }
    return best;
}
/* ────────────────────────────────────────────────────────────────────────────
 * 5. Warnings — the fallback layer only
 * ──────────────────────────────────────────────────────────────────────────── */
function describe(official, id) {
    return official === undefined ? id : `${official.surname} (${official.country})`;
}
function collectWarnings(slots, fixtures, officials, config, unfilledCause) {
    const byId = new Map(officials.map((official) => [official.tms_person_id, official]));
    const warnings = [];
    // 1. Residual, unresolvable SJ/TJ imbalance — the CC-Q3 fallback surface. §06 rule
    //    `Judge SJ / TJ Balance`: switched off, there is no imbalance to report.
    const judgeCounts = !config.judge_sj_tj_balance_enabled
        ? []
        : [...countJudgeDuties(slots).entries()].sort(([a], [b]) => compareStrings(a, b));
    for (const [officialId, counts] of judgeCounts) {
        const difference = Math.abs(counts.sj - counts.tj);
        if (difference <= config.judge_sj_tj_tolerance)
            continue;
        warnings.push({
            code: "sj_tj_imbalance",
            severity: "attention",
            message: `${describe(byId.get(officialId), officialId)} holds ${String(counts.sj)} scoring-judge ` +
                `and ${String(counts.tj)} timing-judge appointments — a difference of ` +
                `${String(difference)}, beyond the tolerance of ` +
                `${String(config.judge_sj_tj_tolerance)}. Redrafting could not resolve it.`,
            official_id: officialId,
            tms_match_db_id: null,
            slot: null,
            detail: { sj: counts.sj, tj: counts.tj, difference, tolerance: config.judge_sj_tj_tolerance },
        });
    }
    // 2. Empty slots. The sentence depends on WHY they are empty — see {@link UnfilledSlotCause}.
    for (const entry of slots) {
        if (entry.official !== null)
            continue;
        const fixture = fixtures[entry.fixtureIndex];
        if (fixture === undefined)
            continue;
        warnings.push({
            code: "unfilled_slot",
            severity: "attention",
            message: unfilledCause === "no_candidate"
                ? `${fixture.match_no} — no eligible official could be drafted as ${entry.slot}. ` +
                    `Appoint someone by hand before pushing.`
                : `${fixture.match_no} — ${entry.slot} not yet appointed. Draft the day or ` +
                    `appoint by hand.`,
            official_id: null,
            tms_match_db_id: fixture.tms_match_db_id,
            slot: entry.slot,
            detail: {
                match_no: fixture.match_no,
                date: fixture.date,
                time: fixture.time,
                cause: unfilledCause,
            },
        });
    }
    // 3. Breaches carried in on locked, hand-made appointments. The engine never proposes one.
    const membersByFixture = new Map();
    for (const entry of slots) {
        if (entry.official === null)
            continue;
        const members = membersByFixture.get(entry.fixtureIndex) ?? [];
        members.push(entry.official);
        membersByFixture.set(entry.fixtureIndex, members);
    }
    for (const entry of slots) {
        if (entry.official === null)
            continue;
        const fixture = fixtures[entry.fixtureIndex];
        if (fixture === undefined)
            continue;
        const official = byId.get(entry.official);
        // §06 rule `Country Conflict`. Off means the warning goes with the rule — migration 015:
        // "switching the rule off removes the warning entirely".
        if (config.country_conflict_enabled &&
            official !== undefined &&
            hasCountryConflict(official, fixture)) {
            warnings.push({
                code: "country_conflict",
                severity: "hard",
                message: `${describe(official, entry.official)} is appointed as ${entry.slot} to ` +
                    `${fixture.match_no}, a match involving their own country. Resolve or override it.`,
                official_id: entry.official,
                tms_match_db_id: fixture.tms_match_db_id,
                slot: entry.slot,
                detail: { match_no: fixture.match_no, country: official.country },
            });
        }
        const members = membersByFixture.get(entry.fixtureIndex) ?? [];
        if (members.filter((id) => id === entry.official).length > 1) {
            warnings.push({
                code: "two_roles_one_match",
                severity: "hard",
                message: `${describe(official, entry.official)} holds more than one role in ` +
                    `${fixture.match_no}. That is impossible and has no override.`,
                official_id: entry.official,
                tms_match_db_id: fixture.tms_match_db_id,
                slot: entry.slot,
                detail: { match_no: fixture.match_no },
            });
        }
    }
    // 4. Daily caps — again only reachable through locked appointments.
    const perDay = new Map();
    for (const entry of slots) {
        if (entry.official === null)
            continue;
        const fixture = fixtures[entry.fixtureIndex];
        if (fixture === undefined)
            continue;
        bump(perDay, `${entry.official}|${fixture.date}`);
    }
    for (const [key, count] of [...perDay.entries()].sort(([a], [b]) => compareStrings(a, b))) {
        const [officialId = "", date = ""] = key.split("|");
        const official = byId.get(officialId);
        const cap = official?.role === "TO" ? config.to_appointments_per_day : config.judge_appointments_per_day;
        if (cap === null || cap === undefined || count <= cap)
            continue;
        warnings.push({
            code: "daily_cap_exceeded",
            severity: "hard",
            message: `${describe(official, officialId)} holds ${String(count)} appointments on ${date}, ` +
                `above the daily cap of ${String(cap)}.`,
            official_id: officialId,
            tms_match_db_id: null,
            slot: null,
            detail: { date, count, cap },
        });
    }
    // 5. Team-exposure heat (§A4 rule 5) — advisory, above the configured threshold, and only
    //    while §06's `Team Exposure Rotation` rule is in force.
    const exposure = new Map();
    for (const entry of config.team_exposure_rotation_enabled ? slots : []) {
        if (entry.official === null)
            continue;
        const fixture = fixtures[entry.fixtureIndex];
        if (fixture === undefined)
            continue;
        for (const team of [fixture.home_team, fixture.away_team]) {
            if (team !== null)
                bump(exposure, `${entry.official}|${team}`);
        }
    }
    for (const [key, count] of [...exposure.entries()].sort(([a], [b]) => compareStrings(a, b))) {
        if (count <= config.team_exposure_heat)
            continue;
        const [officialId = "", team = ""] = key.split("|");
        warnings.push({
            code: "team_exposure_heat",
            severity: "advisory",
            message: `${describe(byId.get(officialId), officialId)} covers ${team} ${String(count)} times, ` +
                `above the exposure threshold of ${String(config.team_exposure_heat)}.`,
            official_id: officialId,
            tms_match_db_id: null,
            slot: null,
            detail: { team, count, threshold: config.team_exposure_heat },
        });
    }
    // 6. Ruling §F — a fixture TMS already reports Official, still carrying appointments of
    //    ours. ADVISORY, deliberately: the hard guard that used to refuse this was removed
    //    (migration 013), because Official is flipped in TMS at the paperwork stage and a
    //    read-only mirror must never refuse what TMS says. This is the quiet Command Desk
    //    anomaly flag that replaced it — recomputed on every response, never stored, never a
    //    409, and never a reason to interrupt a sync.
    fixtures.forEach((fixture, fixtureIndex) => {
        if (fixture.is_official !== true)
            return;
        const held = slots.filter((entry) => entry.fixtureIndex === fixtureIndex && entry.official !== null).length;
        if (held === 0)
            return;
        warnings.push({
            code: "fixture_already_official",
            severity: "advisory",
            message: `${fixture.match_no} is already Official in TMS, and this system still holds ` +
                `${String(held)} technical appointment${held === 1 ? "" : "s"} for it. TMS is the ` +
                `record — nothing is blocked. Check the appointments were published before the ` +
                `match, then leave the fixture alone.`,
            official_id: null,
            tms_match_db_id: fixture.tms_match_db_id,
            slot: null,
            detail: { match_no: fixture.match_no, appointments: held, date: fixture.date },
        });
    });
    return warnings;
}
const WARNING_ORDER = [
    "sj_tj_imbalance",
    "unfilled_slot",
    "country_conflict",
    "two_roles_one_match",
    "daily_cap_exceeded",
    "team_exposure_heat",
    "co_appointment_concentration",
    "fixture_already_official",
];
function sortWarnings(warnings) {
    return [...warnings].sort((a, b) => WARNING_ORDER.indexOf(a.code) - WARNING_ORDER.indexOf(b.code) ||
        compareStrings(a.official_id ?? "", b.official_id ?? "") ||
        (a.tms_match_db_id ?? 0) - (b.tms_match_db_id ?? 0) ||
        compareStrings(a.slot ?? "", b.slot ?? ""));
}
/* ────────────────────────────────────────────────────────────────────────────
 * 6. Public entry points
 * ──────────────────────────────────────────────────────────────────────────── */
function toResult(slots, fixtures, officials, config, passes, 
/** Which `unfilled_slot` sentence fits how these slots came to be empty. */
unfilledCause) {
    const appointments = [...slots]
        .sort((a, b) => a.fixtureIndex - b.fixtureIndex ||
        TECHNICAL_SLOTS.indexOf(a.slot) - TECHNICAL_SLOTS.indexOf(b.slot))
        .flatMap((entry) => {
        const fixture = fixtures[entry.fixtureIndex];
        if (fixture === undefined)
            return [];
        return [
            {
                tms_match_db_id: fixture.tms_match_db_id,
                match_no: fixture.match_no,
                competition: fixture.competition,
                date: fixture.date,
                time: fixture.time,
                slot: entry.slot,
                official_id: entry.official,
                origin: entry.locked ? "td_manual" : "ai_draft",
            },
        ];
    });
    const warnings = sortWarnings(collectWarnings(slots, fixtures, officials, config, unfilledCause));
    return {
        appointments,
        warnings,
        redraft_passes: passes,
        // With §06's `Judge SJ / TJ Balance` rule switched off there is no balance requirement to
        // fall short of, so the draft is not "unbalanced" — it is unmeasured, and reports as
        // satisfied rather than as a failure nobody asked for.
        balanced: !config.judge_sj_tj_balance_enabled ||
            imbalanceCost(slots, config.judge_sj_tj_tolerance) === 0,
        workloads: summariseWorkload(appointments),
    };
}
function prepare(input) {
    const config = { ...DEFAULT_ENGINE_CONFIG, ...(input.config ?? {}) };
    const fixtures = [...input.fixtures].sort(compareFixtures);
    const officials = [...input.officials].sort((a, b) => compareStrings(a.tms_person_id, b.tms_person_id));
    const rotationOffset = input.rotation_offset ?? 0;
    const orderIndex = buildOrderIndex(officials, input.official_order, rotationOffset);
    return {
        fixtures,
        officials,
        config,
        locked: input.locked ?? [],
        orderIndex,
        draftable: input.draft_scope === undefined ? null : new Set(input.draft_scope),
        rotationOffset,
    };
}
/**
 * The **first pass only** — exported for tests and diagnostics, never for the TD's screen.
 * It carries whatever SJ/TJ split the greedy happens to produce; CC-Q3 forbids showing this
 * outcome to the TD. Use {@link draftAppointments} everywhere else.
 */
export function draftAppointmentsSinglePass(input) {
    const { fixtures, officials, config, locked, orderIndex, draftable, rotationOffset } = prepare(input);
    const slots = draftPass(fixtures, officials, config, locked, orderIndex, draftable, rotationOffset);
    return toResult(slots, fixtures, officials, config, 0, "no_candidate");
}
/**
 * **The engine.** Drafts TO, SJ and TJ for every supplied fixture, then re-draws until the
 * SJ/TJ split is inside the configured tolerance for every judge, or until no re-draw can
 * improve it. Only what survives that loop reaches {@link DraftResult.warnings} (CC-Q3).
 *
 * Deterministic: identical inputs always produce an identical result.
 */
export function draftAppointments(input) {
    const { fixtures, officials, config, locked, orderIndex, draftable, rotationOffset } = prepare(input);
    const slots = draftPass(fixtures, officials, config, locked, orderIndex, draftable, rotationOffset);
    // §06 rule `Judge SJ / TJ Balance`. Switched off, the redraft loop does not run: CC-Q3's
    // re-draw exists to satisfy a rule, and a rule not in force is not a reason to move
    // anybody's appointments.
    let passes = 0;
    while (config.judge_sj_tj_balance_enabled && passes < config.max_redraft_passes) {
        const swap = bestSwap(slots, config.judge_sj_tj_tolerance);
        if (swap === null)
            break;
        const sjSlot = slots[swap.sjIndex];
        const tjSlot = slots[swap.tjIndex];
        const carry = sjSlot.official;
        sjSlot.official = tjSlot.official;
        tjSlot.official = carry;
        passes += 1;
    }
    return toResult(slots, fixtures, officials, config, passes, "no_candidate");
}
/**
 * **Measure, do not draft.** Runs the engine's measuring half — the SJ/TJ imbalance test,
 * unfilled slots, country conflicts, two-roles-one-match, daily caps, team-exposure heat and
 * the §F already-Official anomaly — over a set of appointments exactly as supplied, choosing
 * nothing and moving nothing.
 *
 * This is the first-class replacement for the documented workaround that used to live in
 * `apps/api/src/repositories/appointments.ts`: passing every stored slot as locked *and*
 * marking every official unavailable on every date in scope, so that the drafting half had
 * no candidate anywhere and could not choose. That worked, and it was a trick; the two
 * produce identical results for identical inputs (asserted in the engine tests) — except the
 * `unfilled_slot` wording, which here says "not yet appointed" rather than the draft's "no
 * eligible official could be drafted", because only a draft pass can have exhausted the pool
 * — and only this one says what it means.
 *
 * The result is a {@link DraftResult} because the shape a caller needs is the same one:
 * `warnings`, `workloads` and `balanced`, plus `appointments` echoing what was measured.
 * `redraft_passes` is always `0` — nothing was redrawn.
 *
 * Pure and deterministic, like everything else here.
 */
export function evaluateAppointments(input) {
    const config = { ...DEFAULT_ENGINE_CONFIG, ...(input.config ?? {}) };
    const fixtures = [...input.fixtures].sort(compareFixtures);
    const officials = [...input.officials].sort((a, b) => compareStrings(a.tms_person_id, b.tms_person_id));
    const held = new Map(input.appointments.map((entry) => [
        slotKey(entry.tms_match_db_id, entry.slot),
        entry.official_id,
    ]));
    // Every fixture carries all three technical slots, filled or not: an unfilled slot is a
    // finding (§A4), not an absence, so it must be present in the working set to be reported.
    // A filled slot is `locked` — nothing here may move it — and an empty one is not, which is
    // exactly the shape the old workaround left behind and keeps the two results identical
    // (modulo the `unfilled_slot` wording noted above).
    const slots = [];
    fixtures.forEach((fixture, fixtureIndex) => {
        for (const slot of TECHNICAL_SLOTS) {
            const official = held.get(slotKey(fixture.tms_match_db_id, slot)) ?? null;
            slots.push({ fixtureIndex, slot, official, locked: official !== null });
        }
    });
    // A stored-empty slot is "not yet appointed", never "no candidate": no draft ran here, so
    // nothing can have exhausted the pool, and claiming it did misreads an undrafted day as a
    // drafting failure.
    return toResult(slots, fixtures, officials, config, 0, "not_yet_appointed");
}
/** Per-official role counts, derived from a draft — never stored (Part C invariant 1). */
export function summariseWorkload(appointments) {
    const counts = new Map();
    for (const appointment of appointments) {
        const id = appointment.official_id;
        if (id === null)
            continue;
        const current = counts.get(id) ?? { to: 0, sj: 0, tj: 0 };
        if (appointment.slot === "TO")
            current.to += 1;
        else if (appointment.slot === "SJ")
            current.sj += 1;
        else
            current.tj += 1;
        counts.set(id, current);
    }
    return [...counts.entries()]
        .sort(([a], [b]) => compareStrings(a, b))
        .map(([official_id, value]) => ({
        official_id,
        to: value.to,
        sj: value.sj,
        tj: value.tj,
        total: value.to + value.sj + value.tj,
        sj_tj_difference: Math.abs(value.sj - value.tj),
    }));
}
/** The judges rule 4 would flag in a given draft — the imbalance test, exposed for reuse. */
export function findSjTjImbalances(appointments, tolerance = DEFAULT_ENGINE_CONFIG.judge_sj_tj_tolerance) {
    return summariseWorkload(appointments).filter((workload) => workload.sj_tj_difference > tolerance && workload.sj + workload.tj > 0);
}
//# sourceMappingURL=appointments-engine.js.map